import {
  Body,
  Box,
  Button,
  Heading,
  Sheet,
  calc,
  useToast,
} from '@hover/blueprint';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useRefreshHomeownerSession } from 'shared/graphql/hooks';
import type { InferType } from 'yup';

import { useProPageContext } from 'src/modules/pro/hooks/useProPageContext';
import { LinkExternal } from 'src/shared/components';
import type { LinkExternalProps } from 'src/shared/components/LinkExternal';
import { PasswordInputWithValidation } from 'src/shared/components/PasswordInputWithValidation/PasswordInputWithValidation';
import { Form as SharedForm } from 'src/shared/components/forms';
import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useOnboardingCreateHomeownerUserMutation } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { External } from 'src/shared/route';

import { useProTranslate } from '../../hooks';
import { makeCreateAccountValidationSchema } from '../../validations/makeValidationSchemas';

export type CreateAccountFormData = InferType<
  ReturnType<typeof makeCreateAccountValidationSchema>
>;

interface FormData {
  password: string | undefined;
}

const FooterLink = (props: LinkExternalProps) => (
  <LinkExternal
    _active={{ color: 'inherit', textDecorationColor: 'inherit' }}
    color="inherit"
    fontWeight="body"
    size={300}
    textDecoration="underline"
    {...props}
  />
);

export const CreateAccountSheet = ({
  onClose,
  photoUploadSheetOnOpen,
}: {
  onClose: () => void;
  photoUploadSheetOnOpen: () => void;
}) => {
  const t = useProTranslate();
  const tShare = useTranslate(TranslationNamespace.SHARED);
  const toast = useToast();
  const [isInvalid, setIsInvalid] = useState(true);
  const { leadData } = useProPageContext();
  const { id, email, firstName, lastName, phoneNumber } = leadData || {};

  const refreshSession = useRefreshHomeownerSession();
  const [createHomeownerUser] = useOnboardingCreateHomeownerUserMutation();

  const initialValues = {
    password: '',
  };
  const validationSchema = makeCreateAccountValidationSchema(tShare);

  const onSubmit = async (formData: FormData) => {
    try {
      const { data } = await createHomeownerUser({
        variables: {
          args: {
            user: {
              email: email || '',
              firstName,
              lastName,
              mobilePhone: phoneNumber,
              password: formData.password,
            },
            leadId: id,
          },
        },
      });

      if (data?.onboardingCreateHomeownerUser?.jwt) {
        const { accessToken, refreshToken } =
          data?.onboardingCreateHomeownerUser?.jwt || {};

        void defaultLocalStorageAdapter.setAccessToken(accessToken);
        void defaultLocalStorageAdapter.setRefreshToken(refreshToken);

        await refreshSession();

        onClose();
        photoUploadSheetOnOpen();
      } else {
        // TODO: error messaging
      }
      toast({
        description: t('leadSheet.infoSubmitted'),
        position: 'top',
        props: { colorScheme: 'dark' },
        status: 'info',
      });
    } catch (e) {
      // TODO: error messaging
    }
  };

  return (
    <>
      <Sheet.Contents
        maxContentHeight={{
          base: calc.subtract('100vh', 'var(--motion-overflow)'),
          tablet: undefined,
        }}
      >
        <Body paddingTop={700} textColor="neutral.850" variant="semiBold">
          {t('createAccountSheet.stepTwo')}
        </Body>
        <Heading size={{ base: 'large', tablet: 'xl' }}>
          {t('createAccountSheet.createAnAccount')}
        </Heading>
        <Body color="neutral.550">
          {t('createAccountSheet.customizeYourInspiration')}
        </Body>
        <SharedForm<CreateAccountFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          <PasswordInputWithValidation setIsInvalid={setIsInvalid} />
          <Button disabled={isInvalid} mt={400} size="large" type="submit">
            {t('createAccountSheet.createAccount')}
          </Button>
        </SharedForm>
        <Box alignItems="center" flexDir="column" mt={300} overflow="hidden">
          <Box display="inline" textAlign="center" width={700}>
            <Body color="neutral.550" size={300}>
              <Trans
                components={{
                  PrivacyPolicyLink: (
                    <FooterLink href={External.PRIVACY_POLICY.path} />
                  ),
                }}
                i18nKey="createAccountSheet.privacyPolicy"
                t={t}
              />
            </Body>
          </Box>
        </Box>
      </Sheet.Contents>
    </>
  );
};
